import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { Ref } from 'vue';
import { useBetBuilderRecommendationsByMatch } from '@/components/betBuilderRecommendation/useBetBuilderRecommendationsByMatch';
import type { IEvent } from '@/interface/IEvent';
import type { IBetBuilderCombination, IBetBuilderCombinationSelection, IBetBuilderMatch } from '@/interface/betBuilder';
import { BetBuilderSelectionStatus } from '@/interface/enum';
import { useToggleStore } from '@/store/toggleStore';

export function useBetBuilderCombinations(
    event: Ref<IEvent>,
    isInBetBuilderMode: Ref<boolean>,
    betBuilderMatch: Ref<IBetBuilderMatch | null | undefined>,
) {
    const { isPrebuiltInMoreMarketEnabled, moreMarketPrebuiltDisplayRule } = storeToRefs(useToggleStore());

    const isPreBuiltQueryEnabled = computed(() => isInBetBuilderMode.value && isPrebuiltInMoreMarketEnabled.value);
    const isLive = computed(() => event.value.isLive);

    const { recommendations, hasRecommendations, isLoaded } = useBetBuilderRecommendationsByMatch(
        event.value.id,
        isLive,
        isPreBuiltQueryEnabled,
        moreMarketPrebuiltDisplayRule,
    );

    const selectionLookup = computed(() => (betBuilderMatch.value?.markets ?? []).reduce((map, market) => {
        market.selections.forEach(selection => map.set(selection.id, { ...selection, market }));
        return map;
    }, new Map<number, IBetBuilderCombinationSelection>()));

    const betBuilderCombinations = computed<IBetBuilderCombination[]>(() => recommendations.value.flatMap((recommendation) => {
        const selections = recommendation.selectionIds
            .map(id => selectionLookup.value.get(id))
            .filter((entry): entry is IBetBuilderCombinationSelection => !!entry);
        const allSelectionsMatched = selections.length === recommendation.selectionIds.length;
        const hasClosedSelection = selections.some(s => s.status === BetBuilderSelectionStatus.Closed);
        if (!allSelectionsMatched || hasClosedSelection) return [];
        return [{
            combinationKey: recommendation.combinationKey,
            selections,
            maxOdds: recommendation.maxOdds,
        }];
    }));

    return {
        betBuilderCombinations,
        hasRecommendations,
        isPrebuiltLoaded: isLoaded,
    };
}
